import { useCallback, useEffect, useRef, useState } from "react";

const IDLE_ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
const WARNING_ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "click"];

const useIdleTimeout = ({
    onIdle,
    idleTime = 10 * 60 * 1000,
    warningTime = 60 * 1000,
    active = true,
}) => {
    const [showWarning, setShowWarning] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);

    const onIdleRef = useRef(onIdle);
    const idleTimeRef = useRef(idleTime);
    const warningTimeRef = useRef(warningTime);

    const idleTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);

    useEffect(() => {
        onIdleRef.current = onIdle;
        idleTimeRef.current = idleTime;
        warningTimeRef.current = warningTime;
    }, [onIdle, idleTime, warningTime]);

    const clearTimers = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const startCountdown = useCallback(() => {
        setShowWarning(true);
        setRemainingSeconds(Math.ceil(warningTimeRef.current / 1000));
        countdownTimerRef.current = setInterval(() => {
            setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : prev));
        }, 1000);
    }, []);

    const resetTimer = useCallback(() => {
        clearTimers();
        setShowWarning(false);
        setRemainingSeconds(0);
        idleTimerRef.current = setTimeout(() => {
            startCountdown();
        }, Math.max(0, idleTimeRef.current - warningTimeRef.current));
    }, [clearTimers, startCountdown]);

    // Arm / disarm the idle timer based on `active`.
    useEffect(() => {
        if (!active) {
            clearTimers();
            setShowWarning(false);
            setRemainingSeconds(0);
            return undefined;
        }

        resetTimer();

        return () => {
            clearTimers();
        };
    }, [active, resetTimer, clearTimers]);

    // Swap activity listeners depending on whether the countdown is showing.
    useEffect(() => {
        if (!active) return undefined;

        const events = showWarning ? WARNING_ACTIVITY_EVENTS : IDLE_ACTIVITY_EVENTS;
        events.forEach((event) => window.addEventListener(event, resetTimer));

        return () => {
            events.forEach((event) => window.removeEventListener(event, resetTimer));
        };
    }, [active, showWarning, resetTimer]);

    // Log out when the countdown reaches zero.
    useEffect(() => {
        if (showWarning && remainingSeconds <= 0) {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
                countdownTimerRef.current = null;
            }
            setShowWarning(false);
            onIdleRef.current();
        }
    }, [showWarning, remainingSeconds]);

    return { showWarning, remainingSeconds, resetTimer };
};

export default useIdleTimeout;
